import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Paper,
  useTheme,
} from "@mui/material";
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  BugReport as BugReportIcon,
} from "@mui/icons-material";
import Chart from "react-apexcharts";
// import { ApexOptions } from "apexcharts";
import { io, Socket } from "socket.io-client";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import BaseScrollable from "@/Components/scrollable/BaseScrollable";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "@/app/hooks";

interface Bug {
  _id: string;
  bugKey: string;
  summary: string;
  severity: string;
  count: number;
  createdAt: string;
  updatedAt: string;
  tags: string[];
  pageUrl: string;
  ipAddress: string;
}

interface LiveFeedProps {
  bugs?: Bug[];
  companyId?: string;
  groupByFilter?: string;
}

// Map severity to display level
const severityToLevel: Record<string, string> = {
  High: "Error",
  Medium: "Warning",
  Low: "Info",
  Lowest: "Debug",
};

const getLevelColor = (severity: string) => {
  const level = severityToLevel[severity] || "Info";
  switch (level) {
    case "Error":
      return "#D91656";
    case "Warning":
      return "#FFB200";
    case "Info":
      return "#2196F3";
    case "Debug":
      return "#9E9E9E";
    default:
      return "#9E9E9E";
  }
};

const getLevelIcon = (severity: string) => {
  const level = severityToLevel[severity] || "Info";
  switch (level) {
    case "Error":
      return <ErrorIcon sx={{ color: "#D91656" }} />;
    case "Warning":
      return <WarningIcon sx={{ color: "#FFB200" }} />;
    case "Info":
      return <InfoIcon sx={{ color: "#2196F3" }} />;
    default:
      return <BugReportIcon sx={{ color: "#9E9E9E" }} />;
  }
};

const LiveFeed: React.FC<LiveFeedProps> = ({ bugs, groupByFilter }) => {
  const theme = useTheme();
  const { t } = useTranslation(["bugherd"]);
  const navigate = useNavigate();
  const { id: projectId } = useParams<{ id: string }>();
  const [liveData, setLiveData] = useState<number[]>([]);
  const [timeLabels, setTimeLabels] = useState<string[]>([]);
  const [socketBugs, setSocketBugs] = useState<Bug[]>([]);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const { companyDetails } = useAppSelector((state) => state.appGlobal);

  // WebSocket integration for real-time bug updates
  useEffect(() => {
    // WebSocket URL from API documentation
    // The /auto-bugs is a namespace, not a path - it should be part of the URL
    const socketUrl = import.meta.env.VITE_API_BASE_URL + "/auto-bugs";

    // Build query parameters with groupBy filter
    const queryParams: any = {
      companyId: companyDetails?.companyId || "", // Ensure companyId is included
      ...(projectId && { projectId: projectId }), // Optional - filter by project
      ...(groupByFilter && { groupBy: groupByFilter }), // Add groupBy filter
    };

    // Connect to WebSocket with query parameters
    const socket = io(socketUrl, {
      query: queryParams,
      transports: ["websocket"], // Use websocket transport as per documentation
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    socketRef.current = socket;

    // Connection event handlers
    socket.on("connect", () => {
      console.log("✅ WebSocket connected to auto-bugs");
      console.log("Socket ID:", socket.id);
      setConnected(true);
    });

    socket.on("disconnect", () => {
      console.log("❌ WebSocket disconnected from auto-bugs");
      setConnected(false);
    });

    socket.on("connect_error", (error) => {
      console.error("❌ WebSocket connection error:", error);
      console.error("Error message:", error.message);
      toast.error(t("Failed to connect to real-time bug updates"));
    });

    // Get initial data on connection
    socket.on("initial-data", (data: any) => {
      console.log("📦 Initial data received:", data);
      if (data.bugs && Array.isArray(data.bugs)) {
        setSocketBugs(data.bugs);
      }
    });

    // Listen for new bugs in real-time
    socket.on("new-auto-bug", (bug: Bug) => {
      console.log("🚨 New bug detected!", bug.bugKey, bug.summary);
      // toast.info(`New bug detected: ${bug.bugKey}`, {
      //   position: "bottom-right",
      //   autoClose: 3000,
      // });

      // Add new bug to the beginning of the list
      setSocketBugs((prev) => [bug, ...prev]);
    });

    // Listen for bug updates (count increments)
    socket.on("auto-bug-updated", (bug: Bug) => {
      console.log("🔄 Bug updated:", bug.bugKey, "Count:", bug.count);

      // Update existing bug in the list
      setSocketBugs((prev) => {
        const index = prev.findIndex((b) => b._id === bug._id);
        if (index !== -1) {
          const updated = [...prev];
          updated[index] = bug;
          return updated;
        }
        return prev;
      });
    });

    // Listen for aggregated updates
    socket.on("auto-bugs-update", (data: any) => {
      console.log("📊 Aggregated update received:", data);
      // This can be used to update dashboard statistics
      // For now, we'll just log it
    });

    // Cleanup on unmount
    return () => {
      console.log("🧹 Cleaning up WebSocket connection");
      socket.disconnect();
      socketRef.current = null;
    };
  }, [projectId, groupByFilter]); // Re-connect when projectId or groupByFilter changes

  // Initialize live chart data from combined bugs
  useEffect(() => {
    const allBugs = [...socketBugs, ...bugs];

    if (allBugs.length === 0) {
      setLiveData([0, 0, 0, 0, 0]);
      setTimeLabels(["--:--", "--:--", "--:--", "--:--", "--:--"]);
      return;
    }

    // Initialize with bug counts from the last 10 time intervals
    const initialData = allBugs.slice(0, 10).map((bug) => bug.count || 1);
    const initialLabels = allBugs.slice(0, 10).map((_, i) => {
      const time = new Date();
      time.setMinutes(time.getMinutes() - (10 - i));
      return time.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    });

    setLiveData(initialData);
    setTimeLabels(initialLabels);
  }, [socketBugs, bugs]);

  // console.log(liveData, "liveDataliveData", bugs);

  // Merge WebSocket bugs with props bugs (WebSocket bugs take priority)
  const allBugs = useMemo(() => {
    const bugMap = new Map<string, Bug>();

    // Add bugs from props first
    bugs.forEach((bug) => bugMap.set(bug._id, bug));

    // Override/add bugs from WebSocket (these are more recent)
    socketBugs.forEach((bug) => bugMap.set(bug._id, bug));

    return Array.from(bugMap.values());
  }, [bugs, socketBugs]);

  // Calculate statistics from all bugs
  // const stats = useMemo(() => {
  //   const errorCount = allBugs.filter((b) => b.severity === "High").length;
  //   const warningCount = allBugs.filter((b) => b.severity === "Medium").length;
  //   const infoCount = allBugs.filter((b) => b.severity === "Low").length;
  //   const totalCount = allBugs.reduce((sum, b) => sum + (b.count || 1), 0);

  //   return { errorCount, warningCount, infoCount, totalCount };
  // }, [allBugs]);

  // Live line chart options
  const liveChartOptions: any = {
    chart: {
      type: "area",
      animations: {
        enabled: true,
        easing: "linear",
        dynamicAnimation: {
          speed: 1000,
        },
      },
      toolbar: { show: false },
      zoom: { enabled: false },
      background: "transparent",
    },
    dataLabels: { enabled: false },
    stroke: {
      curve: "smooth",
      width: 3,
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.2,
        stops: [0, 90, 100],
      },
    },
    colors: ["#D91656"],
    xaxis: {
      categories: timeLabels,
      title: {
        text: t("Time"),
        style: {
          color: theme.palette.text.secondary,
          fontSize: "12px",
          fontWeight: 500,
        },
      },
      labels: {
        show: true,
        style: {
          colors: theme.palette.text.secondary,
          fontSize: "12px",
        },
        rotate: 0,
      },
      axisBorder: {
        show: true,
        color: theme.palette.divider,
      },
      axisTicks: {
        show: true,
        color: theme.palette.divider,
      },
    },
    yaxis: {
      show: true,
      title: {
        text: t("Bug Count"),
        style: {
          color: theme.palette.text.secondary,
          fontSize: "12px",
          fontWeight: 500,
        },
      },
      labels: {
        show: true,
        style: {
          colors: theme.palette.text.secondary,
          fontSize: "12px",
        },
        formatter: (value: number) => Math.round(value).toString(),
      },
      axisBorder: {
        show: true,
        color: theme.palette.divider,
      },
      axisTicks: {
        show: true,
        color: theme.palette.divider,
      },
    },
    grid: {
      borderColor: theme.palette.divider,
      strokeDashArray: 4,
    },
    tooltip: {
      theme: theme.palette.mode,
    },
  };

  // Severity distribution pie chart
  // const severityChartOptions: ApexOptions = {
  //   chart: {
  //     type: "donut",
  //     background: "transparent",
  //   },
  //   labels: ["Error", "Warning", "Info"],
  //   colors: ["#D91656", "#FFB200", "#2196F3"],
  //   legend: {
  //     position: "bottom",
  //     labels: {
  //       colors: theme.palette.text.primary,
  //     },
  //   },
  //   dataLabels: {
  //     enabled: true,
  //   },
  //   plotOptions: {
  //     pie: {
  //       donut: {
  //         size: "60%",
  //         labels: {
  //           show: true,
  //           total: {
  //             show: true,
  //             label: "Total",
  //             color: theme.palette.text.primary,
  //           },
  //         },
  //       },
  //     },
  //   },
  // };

  // Recent bugs for live feed list (use all bugs, sorted by updatedAt)
  const recentBugs = useMemo(() => {
    return allBugs
      .sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      )
      .slice(0, 5);
  }, [allBugs]);

  return (
    <BaseScrollable defaultHeight="calc(100vh - 200px)" px={2} py={2}>
      {/* Stats Cards */}
      {/* <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #D91656 0%, #ff4081 100%)",
              color: "white",
            }}
          >
            <CardContent>
              <Typography variant="h4" fontWeight={700}>
                {total}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Errors
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #FFB200 0%, #ffc107 100%)",
              color: "white",
            }}
          >
            <CardContent>
              <Typography variant="h4" fontWeight={700}>
                {stats.warningCount}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Warnings
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #2196F3 0%, #64b5f6 100%)",
              color: "white",
            }}
          >
            <CardContent>
              <Typography variant="h4" fontWeight={700}>
                {stats.infoCount}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Info
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
              color: "white",
            }}
          >
            <CardContent>
              <Typography variant="h4" fontWeight={700}>
                {total}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Total Occurrences
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid> */}

      {/* Charts Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Live Bug Activity Chart */}
        <Grid item xs={12} md={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t("Live Bug Activity")}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 2, display: "block" }}
              >
                {t("Real-time bug occurrences")}{" "}
                {connected && (
                  <Chip
                    label={t("Live")}
                    size="small"
                    color="success"
                    sx={{ ml: 1, height: 18 }}
                  />
                )}
              </Typography>
              <Chart
                options={liveChartOptions}
                series={[{ name: t("Bug Count"), data: liveData }]}
                type="area"
                height={250}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Severity Distribution */}
        {/* <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Severity Distribution
              </Typography>
              <Chart
                options={severityChartOptions}
                series={[total, stats.warningCount, stats.infoCount]}
                type="donut"
                height={250}
              />
            </CardContent>
          </Card>
        </Grid> */}
      </Grid>

      {/* Recent Bug Feed */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {t("Recent Bug Feed")}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 2, display: "block" }}
          >
            {t("Latest bugs detected in real-time")}
          </Typography>

          {recentBugs.length === 0 ? (
            <Typography color="text.secondary" textAlign="center" py={4}>
              {t("No bugs detected yet")}
            </Typography>
          ) : (
            recentBugs.map((bug) => (
              <Paper
                key={bug._id}
                variant="outlined"
                onClick={() =>
                  navigate(
                    `/defect-tracker/projects/${projectId}/system-bugs/${bug._id}`,
                  )
                }
                sx={{
                  p: 2,
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  borderLeft: `4px solid ${getLevelColor(bug.severity)}`,
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                {getLevelIcon(bug.severity)}
                <Box flex={1}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={600}>
                      {bug.bugKey}
                    </Typography>
                    <Chip
                      label={t(severityToLevel[bug.severity] || bug.severity)}
                      size="small"
                      sx={{
                        backgroundColor: getLevelColor(bug.severity),
                        color: "white",
                        height: 20,
                        fontSize: "0.7rem",
                      }}
                    />
                    <Chip
                      label={`×${bug.count}`}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: "0.7rem" }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "600px",
                    }}
                  >
                    {bug.summary}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {new Date(bug.createdAt).toLocaleTimeString()}
                </Typography>
              </Paper>
            ))
          )}
        </CardContent>
      </Card>
    </BaseScrollable>
  );
};

export default LiveFeed;
